import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import L from "leaflet";
import MapPolygonUtils from "../../../utils/MapPolygonUtils";

export const useWidgetMarkersMap = ({ centerProp, markers, hasActiveFilters, zoomCoordinates }) => {
  const navigate = useNavigate();
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [mapCenter, setMapCenter] = useState(centerProp);
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);
  const didInitRef = useRef(false);

  // Stores map reference when Leaflet map is ready
  const setMapRef = useCallback((map) => {
    mapRef.current = map;
  }, []);

  // Fits map bounds to include all markers with zoom limit of 15
  const fitMapToMarkers = useCallback(() => {
    if (!mapRef.current || markers.length === 0) return;
    const bounds = L.latLngBounds([]);
    markers.forEach((marker) => MapPolygonUtils.extendBoundsWithMarker(bounds, marker));
    if (!bounds.isValid()) return;
    mapRef.current.fitBounds(bounds);
    const maxZoom = 15;
    mapRef.current.setMaxZoom(maxZoom);
    mapRef.current.on("zoomend", function onZoomEnd() {
      if (mapRef.current.getZoom() > maxZoom) {
        mapRef.current.setZoom(maxZoom);
      }
      mapRef.current.off("zoomend", onZoomEnd);
      mapRef.current.setMaxZoom(18);
    });
    setMapCenter(bounds.getCenter());
  }, [markers]);

  // Handles map initialization and zoom/fit logic based on provided coordinates, filters and map type
  useEffect(() => {
    if (!mapRef.current) return;
    const currentZoom = mapRef.current.getZoom();
    const safeCurrentZoom = Number.isFinite(currentZoom) ? currentZoom : 12;

    if (zoomCoordinates) {
      // If zoom coordinates are provided, zoom to that location
      const center = [zoomCoordinates.lat, zoomCoordinates.lng];
      setMapCenter(center);
      mapRef.current.setView(center, 15);
    } else if (hasActiveFilters && markers.length > 0) {
      // If filters are active, fit the map to all markers
      fitMapToMarkers();
    } else {
      // If no filters and no explicit zoom target:
      // - On first init, center on default/prop.
      // - Afterwards, don't touch center/zoom at all (keep current view).
      if (!didInitRef.current) {
        const center = centerProp ? [centerProp.lat, centerProp.lng] : [32.0853, 34.7818];
        setMapCenter(center);
        const targetZoom = safeCurrentZoom;
        mapRef.current.setView(center, targetZoom);
        didInitRef.current = true;
      }
    }
  }, [markers, hasActiveFilters, centerProp, zoomCoordinates, fitMapToMarkers]);

  // Clears selected marker when it is no longer in the markers list
  useEffect(() => {
    if (!selectedMarker) return;
    const selectedId = selectedMarker.terminalID ?? selectedMarker.erezSKU;
    if (!markers.some((marker) => (marker.terminalID ?? marker.erezSKU) === selectedId)) {
      setSelectedMarker(null);
    }
  }, [markers, selectedMarker]);

  const handleMarkerClick = (marker) => {
    setSelectedMarker(marker);
  };

  const handleCloseMarkerClick = () => {
    setSelectedMarker(null);
  };

  // Handle clicks outside the map container
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mapContainerRef.current && !mapContainerRef.current.contains(event.target)) {
        setSelectedMarker(null);
      }
    };

    // Add event listener when there's a selected marker
    if (selectedMarker) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    // Cleanup event listener
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [selectedMarker]);

  return {
    mapCenter,
    handleCloseMarkerClick,
    setMapRef,
    handleMarkerClick,
    selectedMarker,
    navigate,
    mapContainerRef,
  };
};
